import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider,
  List, ListItem, ListItemText, ListItemAvatar, Avatar, Grid, Tabs, Tab,
} from '@mui/material';
import {
  Gavel, Send, Visibility, CheckCircle, PersonAdd,
} from '@mui/icons-material';
import { disputesAPI } from '../../api/client';
import { format } from 'date-fns';
import { decimalOnly } from '../../utils/inputHelpers';

const statusColors = {
  open: 'warning', under_review: 'info', mediation: 'secondary',
  resolved: 'success', closed: 'default', escalated: 'error',
};

function AdminDisputesPage() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolution, setResolution] = useState('');
  const [resolutionAmount, setResolutionAmount] = useState('');

  const statusFilters = [null, 'open', 'under_review', 'mediation', 'resolved', 'closed'];
  const tabLabels = ['All', 'Open', 'Under Review', 'Mediation', 'Resolved', 'Closed'];

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const params = { per_page: 100 };
      if (statusFilters[tab]) params.status = statusFilters[tab];
      const res = await disputesAPI.list(params);
      setDisputes(res.data.disputes || []);
    } catch (err) {
      setError('Failed to load disputes');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDisputes(); }, [tab]);

  const handleView = async (dispute) => {
    setSelectedDispute(dispute);
    setViewOpen(true);
    try {
      const res = await disputesAPI.getMessages(dispute.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setMessages([]); }
  };

  const handleSendMsg = async () => {
    if (!newMsg.trim() || !selectedDispute) return;
    try {
      await disputesAPI.addMessage(selectedDispute.id, { message: newMsg.trim(), is_internal: false });
      setNewMsg('');
      const res = await disputesAPI.getMessages(selectedDispute.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setError('Failed to send message'); }
  };

  const handleResolve = async () => {
    if (!resolution.trim() || !selectedDispute) return;
    try {
      await disputesAPI.resolve(selectedDispute.id, resolution.trim(), resolutionAmount ? parseFloat(resolutionAmount) : undefined);
      setSuccess('Dispute resolved successfully');
      setResolveOpen(false);
      setViewOpen(false);
      setResolution('');
      setResolutionAmount('');
      fetchDisputes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resolve dispute');
    }
  };

  const handleStatusChange = async (dispute, newStatus) => {
    try {
      await disputesAPI.update(dispute.id, { status: newStatus });
      setSuccess(`Dispute status updated to ${newStatus}`);
      fetchDisputes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Dispute Management</Typography>
      </Stack>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        {tabLabels.map((label, i) => <Tab key={i} label={label} />)}
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : disputes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Gavel sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No disputes found</Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Dispute #</strong></TableCell>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Filed</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disputes.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{d.dispute_number}</Typography></TableCell>
                  <TableCell>{d.subject}</TableCell>
                  <TableCell><Chip label={d.category?.replace('_', ' ')} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={d.status?.replace('_', ' ')} size="small" color={statusColors[d.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><Chip label={d.priority} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell>{d.created_at ? format(new Date(d.created_at), 'dd/MM/yyyy') : '-'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={() => handleView(d)} title="View"><Visibility fontSize="small" /></IconButton>
                      {d.status === 'open' && (
                        <IconButton size="small" color="info" onClick={() => handleStatusChange(d, 'under_review')} title="Start Review"><PersonAdd fontSize="small" /></IconButton>
                      )}
                      {['open', 'under_review', 'mediation'].includes(d.status) && (
                        <IconButton size="small" color="success" onClick={() => { setSelectedDispute(d); setResolveOpen(true); }} title="Resolve"><CheckCircle fontSize="small" /></IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Dispute {selectedDispute?.dispute_number}</Typography>
            <Chip label={selectedDispute?.status?.replace('_', ' ')} color={statusColors[selectedDispute?.status] || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight={600}>{selectedDispute?.subject}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{selectedDispute?.description}</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Category</Typography><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedDispute?.category?.replace('_', ' ')}</Typography></Grid>
            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Priority</Typography><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedDispute?.priority}</Typography></Grid>
            <Grid item xs={4}><Typography variant="caption" color="text.secondary">Filed</Typography><Typography variant="body2">{selectedDispute?.created_at ? format(new Date(selectedDispute.created_at), 'dd/MM/yyyy HH:mm') : '-'}</Typography></Grid>
          </Grid>

          {selectedDispute?.resolution && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Resolution</Typography>
              <Typography variant="body2">{selectedDispute.resolution}</Typography>
              {selectedDispute.resolution_amount && <Typography variant="body2">Amount: GHS {selectedDispute.resolution_amount}</Typography>}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Messages</Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {messages.length === 0 ? (
              <ListItem><ListItemText secondary="No messages yet" /></ListItem>
            ) : messages.map((m) => (
              <ListItem key={m.id} alignItems="flex-start">
                <ListItemAvatar><Avatar sx={{ width: 32, height: 32, bgcolor: m.is_internal ? 'warning.main' : 'primary.main' }}>{m.is_internal ? 'I' : 'U'}</Avatar></ListItemAvatar>
                <ListItemText
                  primary={<>{m.message} {m.is_internal && <Chip label="Internal" size="small" color="warning" sx={{ ml: 1 }} />}</>}
                  secondary={m.created_at ? format(new Date(m.created_at), 'dd/MM/yy HH:mm') : ''}
                />
              </ListItem>
            ))}
          </List>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField fullWidth size="small" placeholder="Type a message..." value={newMsg} onChange={(e) => setNewMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendMsg()} />
            <IconButton color="primary" onClick={handleSendMsg}><Send /></IconButton>
          </Stack>
        </DialogContent>
        <DialogActions>
          {['open', 'under_review', 'mediation'].includes(selectedDispute?.status) && (
            <Button variant="contained" color="success" onClick={() => { setResolveOpen(true); }} startIcon={<CheckCircle />}>Resolve</Button>
          )}
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Dispute</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Resolution Details" multiline rows={4} value={resolution} onChange={(e) => setResolution(e.target.value)} />
            <TextField fullWidth label="Resolution Amount (GHS, optional)" type="number" value={resolutionAmount} onChange={(e) => setResolutionAmount(e.target.value)} onKeyDown={decimalOnly} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleResolve} disabled={!resolution.trim()}>Resolve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminDisputesPage;
